/*
 * ---------------------------------------------------------------------------
 *
 * COPYRIGHT (c) 2026 IBS Software Canada Inc. All Rights Reserved.
 *
 * The copyright to the computer program(s) herein is the property of
 * IBS Software Canada Inc. The program(s) may be used and/or copied
 * only with the written permission from IBS Software Canada Inc.
 * or in accordance with the terms and conditions stipulated in the
 * agreement/contract under which the program(s) have been supplied.
 *
 * Date  : 2026-01-29
 *
 * ---------------------------------------------------------------------------
 */

import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { withStyles } from '@material-ui/core/styles';
import { Card, CardTitle } from 'material-ui/Card';
import { fetchBidPreferences as fetchBidPreferencesAction, updateBidSessionSuccess, savePreference as savePreferenceAction, fetchCrewGroups as fetchCrewGroupsAction, getNextAvailableLevel, switchPriority as switchPriorityAction, updateAllScores as updateAllScoresAction, updateWeight as updateWeightAction, updateWeightForAll as updateWeightForAllAction } from '../../../data/bidSession/productionActions';
import { BidMode, saveBidMode, movePreference, sortPreferences, processPreferences, moveItem, getLastBidScopeUsed, isOptionLevelIsSmallerEqualThanReserve, getBidMode, OPTION_LEVEL_WEIGHTED_PREFERENCE } from '../../BidSession/helpers';
import MainCardContainer from '../../../components/MainCardContainer';
import CardRow from '../../../components/CardRow';
import BidTable from '../../BidSession/components/BidTable';
import Loader from '../../../components/Loader';
import Notification from '../../../components/Notification';
import { DefaultButton } from '../../../components/Buttons';
import { Link } from 'react-router-dom';
import * as bidClassesProductionActions from '../../../data/bidClasses/productionActions';
import apiCall from '../../../data/app/apiCall';
import {
  STDBIDSESSION_ID,
  REGULAR_BID_SCOPE,
  RESERVE_DOWN_TO,
  httpStatus,
  LAST_BID_SCOPE_USE
} from '../../../data/constants';
import {
  getStaffId,
  orderPreferences,
} from '../../BidSession/helpers';
import { LanguageContext } from '../../../context/LanguageContext';

import '../styles.scss';

//sessionStorage key constant
const CREW_GROUP_DEFAULT_BIDSESSION_ID = 'CREW_GROUP_DEFAULT_BIDSESSION_ID';

// Use LanguageContext for all labels
const useLabels = () => {
  const { labels } = useContext(LanguageContext);
  return {
    commonLabels: labels.commonLabels,
    CREW_GROUP_DEFAULT: labels.CREW_GROUP_DEFAULT,
    BID_TABLE: labels.BID_TABLE,
    SETTINGS: labels.SETTINGS,
    BID_SCOPE: labels.SETTINGS?.DIALOG_BID_SCOPE,
    IMPERSONATE: labels.IMPERSONATE || {
      SELECT_BUTTON: 'Select',
      CLEAR_BUTTON: 'Clear',
      HEAD_TITLE: 'Welcome Administrator',
      CARD_TITLE: 'Staff Selection',
      HINT_TEXT: 'Select a staff'
    }
  };
};

const AutocompleteStyled = withStyles({
  inputRoot: {
    width: '400px',
    height: '40px',
    padding: '0px !important'
  }
})(Autocomplete);

const SelectButton = withStyles({
  root: {
    backgroundColor: 'transparent',
    borderColor: '#5396E4',
    height: '40px'
  }
})(DefaultButton);

const CrewGroupDefault = (props) => {
  const {
    commonLabels,
    CREW_GROUP_DEFAULT,
    BID_TABLE,
    SETTINGS,
    BID_SCOPE,
    IMPERSONATE
  } = useLabels();
  // Initialize bidScope from sessionStorage to match BidTable's state
  const initialBidScope = sessionStorage.getItem(LAST_BID_SCOPE_USE) || REGULAR_BID_SCOPE;

  const [state, setState] = useState({
    selectedCrewGroup: null,
    crewGroups: [],
    allBidPreferences: [],
    bidPreferences: { entries: [] },
    bidLevels: { entries: [] },
    lastSave: null,
    loadingBidTable: false,
    loadingCrewGroups: true,
    statusDisplay: false,
    statusDisplayText: '',
    bidScope: initialBidScope,
    showBidsSection: false, // Track if bids section should be displayed
    bidTableKey: 0, // Key to force BidTable re-mount when scope changes
    selectedBidSessionId: null, // Store bidSessionId for selected crew group
    bidMode: '' // Will be fetched dynamically from API
  });

  const normalizeCrewGroupLabel = (name = '') => {
    if (!name) return '';

    const cleaned = name.replace(/^\s*crew\s*group\s*default\s*[:\-–—]?\s*/i, '').trim();

    if (!cleaned) return name.trim();

    return cleaned;
  };

  const bidSessionRef = useRef({
    currentBid: {
      bidSessionId: sessionStorage.getItem(STDBIDSESSION_ID)
    },
    user: {},
    bidMode: ''
  });

  const isMountedRef = useRef(true);

  useEffect(() => {
    const loadCrewGroups = async () => {
      try {
        // Use imported fetchCrewGroupsAction directly
        const response = await fetchCrewGroupsAction()();
        if (response && response.data && response.data.entries) {

          const crewGroups = response.data.entries.map(group => {
            const normalizedLabel = normalizeCrewGroupLabel(group.name);
            return {
              label: normalizedLabel,
              value: group.bidSessionId || group.id,
              bidSessionId: group.bidSessionId || group.id
            };
          }).sort((a, b) => a.label.localeCompare(b.label));

          if (isMountedRef.current) {
            setState(prev => ({
              ...prev,
              crewGroups,
              loadingCrewGroups: false
            }));
          }
        } else {
          if (isMountedRef.current) {
            setState(prev => ({
              ...prev,
              loadingCrewGroups: false
            }));
            showNotification(CREW_GROUP_DEFAULT.ERROR_LOADING_CREW_GROUPS);
          }
        }
      } catch (error) {
        console.error('Error fetching crew groups:', error);
        if (isMountedRef.current) {
          setState(prev => ({
            ...prev,
            loadingCrewGroups: false
          }));
          showNotification(CREW_GROUP_DEFAULT.ERROR_LOADING_CREW_GROUPS);
        }
      }
    };
    loadCrewGroups();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Set up bid session reference
  useEffect(() => {
    if (props.user) {
      bidSessionRef.current.user = props.user;
    }
  }, [props.user]);

  const handleCrewGroupChange = (event, newValue) => {
    if (newValue && newValue.bidSessionId) {
      sessionStorage.setItem('CREW_GROUP_DEFAULT_BIDSESSION_ID', newValue.bidSessionId);
    }
    setState(prev => ({
      ...prev,
      selectedCrewGroup: newValue || null,
      selectedBidSessionId: newValue ? (newValue.bidSessionId || newValue.value || null) : null,
      showBidsSection: false
    }));
  };

  const handleSelectClick = async () => {
    if (state.selectedCrewGroup) {
      if (state.selectedCrewGroup.bidSessionId) {
        sessionStorage.setItem('CREW_GROUP_DEFAULT_BIDSESSION_ID', state.selectedCrewGroup.bidSessionId);
      }
      sessionStorage.setItem(LAST_BID_SCOPE_USE, state.bidScope);
      setState(prev => ({
        ...prev,
        loadingBidTable: true,
        showBidsSection: true
      }));
      try {
        const bidSessionId = state.selectedCrewGroup.bidSessionId;
        const staffId = getStaffId();

        // Fetch bid mode from API to support all modes
        const bidModeResponse = await apiCall(`preferences/bid-session-mode?bidsession_id=${bidSessionId}&staff_id=${staffId}`, 'get');
        let bidMode = bidModeResponse && bidModeResponse.data ? bidModeResponse.data : BidMode.LADDER.biddingMode;
        
        // Save the bid mode to session storage
        saveBidMode(bidMode);
        
        // Update bidSessionRef with the fetched bid mode
        bidSessionRef.current.bidMode = bidMode;

        // Use fetchBidPreferencesAction to fetch bid preferences
        const prefsResponse = await props.fetchBidPreferences(staffId, bidSessionId);

        // Load levels based on bid mode - ladder mode requires levels, weight modes don't need them
        let levelsResponse = null;
        let bidLevels = { entries: [] };
        let nextAvailableLevel = null;
        
        if (bidMode === BidMode.LADDER.biddingMode) {
          // Only fetch ladder levels if bid mode is LADDER
          levelsResponse = await apiCall(`preferences/bids/ladder/levels?bidsession_id=${bidSessionId}&staff_id=${staffId}`, 'get');
          if (levelsResponse && levelsResponse.data && levelsResponse.data.entries) {
            bidLevels = levelsResponse.data;
          }
        }
        
        // Build ALL preferences (no filtering - BidTable filters internally)
        let allBidPreferences = [];
        if (prefsResponse && prefsResponse.data && Array.isArray(prefsResponse.data.entries)) {
          allBidPreferences = orderPreferences(prefsResponse.data.entries);
        }
        
        // Compute next available level only for LADDER mode
        if (bidMode === BidMode.LADDER.biddingMode) {
          nextAvailableLevel = getNextAvailableLevel(
            { entries: allBidPreferences },
            bidLevels,
            false // allowDuplicates: false for Crew Group Default
          );
        }

        setState(prev => ({
          ...prev,
          allBidPreferences,
          bidPreferences: {
            start: 0,
            end: allBidPreferences.length - 1,
            totalEntries: allBidPreferences.length,
            entries: allBidPreferences
          },
          bidLevels,
          nextAvailableLevel,
          bidMode,
          loadingBidTable: false
        }));
      } catch (error) {
        console.error('Error in handleSelectClick:', error);
        setState(prev => ({ ...prev, loadingBidTable: false }));
        showNotification(CREW_GROUP_DEFAULT.ERROR_LOADING_BIDS);
      }
    }
  };

  const showNotification = (message) => {
    if (isMountedRef.current) {
      setState(prev => ({
        ...prev,
        statusDisplay: true,
        statusDisplayText: message
      }));
    }
  };

  const handleCloseNotification = () => {
    if (isMountedRef.current) {
      setState(prev => ({
        ...prev,
        statusDisplay: false,
        statusDisplayText: ''
      }));
    }
  };

  const handleActionPreference = (response) => {
    if (response && response.status === 200 && isMountedRef.current) {
      let allBidPreferencesArr = [];
      if (response.data.preferences) {
        // Old format (array)
        allBidPreferencesArr = orderPreferences(response.data.preferences);
      } else if (response.data.entries) {
        // New format (array)
        allBidPreferencesArr = orderPreferences(response.data.entries);
      }
      const lastSave = response.data.revision?.lastSave;
      // Store ALL preferences - let BidTable handle filtering by scope
      setState(prev => ({
        ...prev,
        lastSave,
        allBidPreferences: allBidPreferencesArr,
        bidPreferences: {
          start: 0,
          end: allBidPreferencesArr.length - 1,
          totalEntries: allBidPreferencesArr.length,
          entries: allBidPreferencesArr
        },
        loadingBidTable: false
      }));
    }
  };

  const handleMovePreference = (type, oldScore, isCombined) => {
    // Use allBidPreferences (unfiltered) since processPreferences filters by getLastBidScopeUsed()
    const preferencesToCheck = processPreferences(
      type,
      oldScore,
      state.allBidPreferences,
      showNotification
    );

    if (!preferencesToCheck) return;

    const { bidAggregateIds, bidAggregateIdsToSwitchWith } = movePreference(
      type,
      oldScore,
      preferencesToCheck,
      isCombined
    );

    const currentPrefs = [...state.allBidPreferences];
    const movingIndex = currentPrefs.findIndex(p => bidAggregateIds.includes(p.bidAggregateId));
    const targetIndex = currentPrefs.findIndex(p => bidAggregateIdsToSwitchWith.includes(p.bidAggregateId));

    if (movingIndex !== -1 && targetIndex !== -1) {
      const movingItems = currentPrefs.filter(p => bidAggregateIds.includes(p.bidAggregateId));
      const targetItems = currentPrefs.filter(p => bidAggregateIdsToSwitchWith.includes(p.bidAggregateId));

      const movingScore = movingItems[0]?.score;
      const targetScore = targetItems[0]?.score;

      // Apply swapped scores
      const optimisticPrefs = currentPrefs.map(pref => {
        if (bidAggregateIds.includes(pref.bidAggregateId)) {
          return { ...pref, score: targetScore };
        }
        if (bidAggregateIdsToSwitchWith.includes(pref.bidAggregateId)) {
          return { ...pref, score: movingScore };
        }
        return pref;
      });

      const sortedOptimistic = sortPreferences(optimisticPrefs);

      // Update UI immediately - store ALL preferences, let BidTable filter
      setState(prev => ({
        ...prev,
        allBidPreferences: sortedOptimistic,
        bidPreferences: {
          start: 0,
          end: sortedOptimistic.length - 1,
          totalEntries: sortedOptimistic.length,
          entries: sortedOptimistic
        }
      }));
    }

    // Call API in background to persist the change
    props.switchPriority(bidAggregateIds, bidAggregateIdsToSwitchWith)
      .then(response => {
        if (response && response.status === httpStatus.OK) {
          // Sync with server data for consistency - store ALL preferences
          const bidPreferences = sortPreferences(response.data.preferences);
          setState(prev => ({
            ...prev,
            allBidPreferences: bidPreferences,
            bidPreferences: {
              start: 0,
              end: bidPreferences.length - 1,
              totalEntries: bidPreferences.length,
              entries: bidPreferences
            }
          }));
        }
        // If not OK, optimistic update already applied - user sees immediate feedback
      })
      .catch(e => {
        console.log(e.statusText);
        // On error, revert to original state - store ALL preferences
        setState(prev => ({
          ...prev,
          allBidPreferences: currentPrefs,
          bidPreferences: {
            start: 0,
            end: currentPrefs.length - 1,
            totalEntries: currentPrefs.length,
            entries: currentPrefs
          }
        }));
        showNotification('Failed to move request. Please try again.');
      });
  };

  const reorderPreferences = (oldIndex, newIndex) => {
    // get the bid scope to filter the preferences to move
    let lastBidScopeUsed = getLastBidScopeUsed();

    // Save original state for potential revert on error
    const originalAllPreferences = [...state.allBidPreferences];

    // Use allBidPreferences (unfiltered) since we filter by lastBidScopeUsed
    //non-movable Preferences
    let fixedPreferences = state.allBidPreferences.filter(
      preference =>
        isOptionLevelIsSmallerEqualThanReserve(preference.optionLevel) ||
        !preference.scoreApplicable
    );

    let preferencesToCheck = state.allBidPreferences.filter(
      preference =>
        preference.preferenceOptionPropertyId === lastBidScopeUsed &&
        preference.optionLevel > RESERVE_DOWN_TO.levelAny &&
        preference.scoreApplicable !== false
    );
    // Adjust indexes for preferencesToCheck
    let preferencesInScope = state.allBidPreferences.filter(
      preference => preference.preferenceOptionPropertyId === lastBidScopeUsed
    );
    let preferencesNotInScope = state.allBidPreferences.filter(
      preference => preference.preferenceOptionPropertyId !== lastBidScopeUsed
    );

    const validIndexes = preferencesInScope.reduce((acc, preference, index) => {
      if (preferencesToCheck.includes(preference)) acc.push(index);
      return acc;
    }, []);

    const adjustedOldIndex = validIndexes.indexOf(oldIndex);
    const adjustedNewIndex = validIndexes.indexOf(newIndex);

    if (adjustedOldIndex === -1 || adjustedNewIndex === -1) return;
    // Move only allowed preferences
    let updatePrefs = moveItem(
      preferencesToCheck,
      adjustedOldIndex,
      adjustedNewIndex
    );

    // Optimistic update: apply UI change immediately without loader - store ALL preferences
    const optimisticAllPrefs = [...fixedPreferences, ...updatePrefs, ...preferencesNotInScope];

    setState(prev => ({
      ...prev,
      allBidPreferences: optimisticAllPrefs,
      bidPreferences: {
        start: 0,
        end: optimisticAllPrefs.length - 1,
        totalEntries: optimisticAllPrefs.length,
        entries: optimisticAllPrefs
      }
    }));

    props.updateAllScores(updatePrefs)
      .then(response => {
        if (response && response.status === httpStatus.OK) {
          // Update state with new scores from server for consistency
          const updatedAllPrefs = [...optimisticAllPrefs];
          response.data.entries.forEach(responseEntry => {
            const responseScore = responseEntry.score;
            const responseAggregateId = responseEntry.bidAggregateId;
            updatedAllPrefs.forEach(currentEntry => {
              if (currentEntry.bidAggregateId === responseAggregateId) {
                currentEntry.score = responseScore;
              }
            });
          });
          const bidPreferences = sortPreferences(orderPreferences(updatedAllPrefs));
          setState(prev => ({
            ...prev,
            allBidPreferences: bidPreferences,
            bidPreferences: {
              start: 0,
              end: bidPreferences.length - 1,
              totalEntries: bidPreferences.length,
              entries: bidPreferences
            }
          }));
        }
      })
      .catch(() => {
        // On error, revert to original state
        setState(prev => ({
          ...prev,
          allBidPreferences: originalAllPreferences,
          bidPreferences: {
            start: 0,
            end: originalAllPreferences.length - 1,
            totalEntries: originalAllPreferences.length,
            entries: originalAllPreferences
          }
        }));
        showNotification('Failed to reorder. Please try again.');
      });
  };

  // Weight sorting function for BidTable
  const weightSorting = (ascending) => {
    const bidMode = getBidMode();

    function sortBidsByCategory(preferences, isAscending) {
      // Separate into three categories
      const noScoreBids = preferences.filter(p => !p.scoreApplicable);
      const conditionalBids = preferences.filter(p => p.scoreApplicable !== false && isOptionLevelIsSmallerEqualThanReserve(p.optionLevel));
      const nonConditionalBids = preferences.filter(p => p.scoreApplicable !== false && p.optionLevel === OPTION_LEVEL_WEIGHTED_PREFERENCE);

      // Sort each category
      if (isAscending) {
        conditionalBids.sort((a, b) => a.optionLevel - b.optionLevel);
        nonConditionalBids.sort((a, b) => parseFloat(a.score) - parseFloat(b.score));
      } else {
        conditionalBids.sort((a, b) => b.optionLevel - a.optionLevel);
        nonConditionalBids.sort((a, b) => parseFloat(b.score) - parseFloat(a.score));
      }
      // Combine in order: non-scoreable, conditional, non-conditional
      return [...noScoreBids, ...conditionalBids, ...nonConditionalBids];
    }

    const sortedPreferences = sortBidsByCategory(state.allBidPreferences, ascending);

    setState(prev => ({
      ...prev,
      allBidPreferences: sortedPreferences,
      bidPreferences: {
        ...prev.bidPreferences,
        entries: sortedPreferences
      }
    }));
  };

  const handleWeightEdit = (preference, weight, bidmode) => {
    if (weight === '') return;

    const prefIds = [];
    const bidAggregateId = preference.bidAggregateId;

    // Update the weight in the local state immediately
    const updatedAllPreferences = state.allBidPreferences.map(pref => {
      const shouldUpdate = (preference.groupName && pref.groupName === preference.groupName) ||
        pref.bidAggregateId === bidAggregateId;

      if (shouldUpdate) {
        prefIds.push(pref.bidAggregateId);
        return { ...pref, score: weight };
      }
      return pref;
    });

    // Update state immediately for instant UI feedback
    setState(prev => ({
      ...prev,
      allBidPreferences: updatedAllPreferences,
      bidPreferences: {
        ...prev.bidPreferences,
        entries: updatedAllPreferences
      }
    }));

    // Validate weight range based on bid mode
    const bidMode = getBidMode();
    const isValidWeight =
      (bidMode === BidMode.AUTOMATIC.biddingMode && weight > 0 && weight < 4) ||
      (bidMode !== BidMode.AUTOMATIC.biddingMode && weight > 0 && weight < 1001);

    if (isValidWeight && prefIds.length > 0) {
      props.updateWeight(preference, weight, bidmode, prefIds).catch((error) => {
        console.error('[handleWeightEdit] Error updating weight:', error);
      });
    }
  };

  const handleUpdateBidSession = async (updatedSession) => {
    // Update local state bidMode if it changed
    if (updatedSession.bidMode && updatedSession.bidMode !== state.bidMode) {
      bidSessionRef.current.bidMode = updatedSession.bidMode;

      // Optimistic update: use scores from updatedSession immediately
      // updatedSession.bidPreferences.entries already has correct weights from buildUpdatedSession
      // This prevents the flicker where weights show old values before API completes
      if (updatedSession.bidPreferences?.entries?.length > 0) {
        const updatedPrefs = updatedSession.bidPreferences.entries;
        setState(prev => ({
          ...prev,
          bidMode: updatedSession.bidMode,
          allBidPreferences: updatedPrefs,
          bidPreferences: {
            ...prev.bidPreferences,
            entries: updatedPrefs
          }
        }));
      } else {
        setState(prev => ({ ...prev, bidMode: updatedSession.bidMode }));
      }

      // Wait for backend API to complete (updateWeightAll), then sync with server
      await new Promise(resolve => setTimeout(resolve, 1000));

      try {
        const bidSessionId = updatedSession.currentBid.bidSessionId;
        const staffId = getStaffId();
        const prefsResponse = await props.fetchBidPreferences(staffId, bidSessionId);

        if (prefsResponse && prefsResponse.data && prefsResponse.data.entries) {
          const allPrefs = prefsResponse.data.entries;

          // Update state with server data (to ensure consistency with backend)
          setState(prev => ({
            ...prev,
            bidMode: updatedSession.bidMode,
            allBidPreferences: allPrefs,
            bidPreferences: {
              ...prev.bidPreferences,
              entries: allPrefs
            }
          }));
        }
        // If no server data, optimistic update already applied - no need for error handling
      } catch (error) {
        console.error('[handleUpdateBidSession] Error fetching preferences:', error);
        // Optimistic update already applied, so UI remains functional
      }
      
      return Promise.resolve();
    } else {
      // No bidMode change, nothing to do (CrewGroupDefault doesn't use Redux bidSession)
      return Promise.resolve();
    }
  };

  const { user } = props;
  const { selectedCrewGroup, crewGroups, bidPreferences, loadingCrewGroups, loadingBidTable } = state;

  // Memoize bidSessionUser to prevent unnecessary re-renders
  const bidSessionUser = useMemo(() => {
    // For Crew Group Default, use staffId from sessionStorage if available
    return {
      ...user,
      currentCategory: {
        categoryId: 0,
        name: selectedCrewGroup?.label || ''
      },
      staffId: sessionStorage.getItem('staffId') || user?.staffId
    };
  }, [user, selectedCrewGroup]);

  // Memoize the bidSession object to prevent infinite re-renders in BidClasses
  const memoizedBidSession = useMemo(() => {
    const result = {
      ...bidSessionRef.current,
      // Provide FULL set to BidTable; it manages filtering internally (like BidSession does)
      bidPreferences: { entries: state.allBidPreferences || [] },
      filteredBidPreferences: { entries: state.allBidPreferences || [] },
      bidLevels: state.bidLevels,
      nextAvailableLevel: state.nextAvailableLevel,
      allowDuplicates: false,
      user: bidSessionUser,
      currentBid: bidSessionRef.current.currentBid || { bidSessionId: sessionStorage.getItem(STDBIDSESSION_ID) },
      bidMode: state.bidMode // Use bidMode from state
    };
    return result;
  }, [state.allBidPreferences, state.bidScope, state.bidLevels, state.nextAvailableLevel, state.bidMode, bidSessionUser]);

  const content = [];

  // Crew Group Selection Card
  content.push(
    <Card key="crew-group-selection-card" className="crew-group-card">
      <CardTitle
        title={CREW_GROUP_DEFAULT.CREW_GROUP_SELECTION_TITLE}
        className='card-title'
      />
      <CardRow
        key={0}
        cardNumber={1}
        cards={[
          <div className='language-bar' key="selection-bar">
            {loadingCrewGroups ? (
              <Loader />
            ) : (
              <div className='buttons-wrapper'>
                <div className='dropdown-wrapper'>
                  <AutocompleteStyled
                    options={crewGroups || []}
                    getOptionLabel={(option) => option.label || ''}
                    getOptionSelected={(option, value) =>
                      option.value === value.value
                    }
                    value={selectedCrewGroup || null}
                    onChange={handleCrewGroupChange}
                    renderInput={(params) => (
                      <TextField {...params} variant='outlined' placeholder={CREW_GROUP_DEFAULT.SELECT_CREW_GROUP} />
                    )}
                  />
                </div>
                <div className='select-button-wrapper'>
                  <SelectButton
                    className={'button-actions'}
                    disabled={!selectedCrewGroup}
                  >
                    <Link
                      to={''}
                      onClick={(e) => { e.preventDefault(); handleSelectClick(); }}
                    >
                      {IMPERSONATE.SELECT_BUTTON}
                    </Link>
                  </SelectButton>
                </div>
              </div>
            )}
          </div>
        ]}
      />
    </Card>
  );

  // Crew Group Default Bids Card (only shown when Select button is clicked)
  if (state.showBidsSection && selectedCrewGroup) {
    // Only render BidTable after loading completes to ensure bidMode is set correctly
    if (!loadingBidTable) {
      content.push(
        <BidTable
          key={`BidTable-crew-group-default`}
          bidSession={{
            ...memoizedBidSession,
            displayScope: true,
            currentBid: {
              ...memoizedBidSession.currentBid,
              bidSessionId: sessionStorage.getItem(CREW_GROUP_DEFAULT_BIDSESSION_ID) || memoizedBidSession.currentBid.bidSessionId
            }
          }}
          bidLevels={state.bidLevels}
          handleActionPreference={handleActionPreference}
          handleMovePreference={handleMovePreference}
          reorderPreferences={reorderPreferences}
          handleWeightEdit={handleWeightEdit}
          weightSorting={weightSorting}
          updateBidSession={handleUpdateBidSession}
          updateWeightAll={props.updateWeightAll}
          savePreference={props.savePreference}
          deletePreference={props.deletePreference}
          fetchClassOptionAttributes={props.fetchClassOptionAttributes}
          title={`${CREW_GROUP_DEFAULT.DEFAULT_REQUESTS} '${selectedCrewGroup.label}'`}
          subtitle={CREW_GROUP_DEFAULT.COMPONENT_SUBTITLE}
          listTitle={BID_TABLE.LIST_TITLE}
          readOnly={false}
          isFetching={loadingBidTable}
          loadingBidTable={loadingBidTable}
          displayCombinedPreferences={false}
          isCrewGroupDefault={true}
          hideCombinedFab={true}
          hideOtherOptions={true}
          hideRLT={true}
          skipDisplayEnabledCheck={true}
          crewGroupId={selectedCrewGroup.value}
          defaultLevel={state.nextAvailableLevel}
        />
      );
    } else {
      // Show loader while data is being fetched
      content.push(
        <Card key="loading-card" className="crew-group-card">
          <Loader />
        </Card>
      );
    }
  }

  return (
    <div>
      <MainCardContainer
        headTitle={CREW_GROUP_DEFAULT.TITLE}
        headSubtitle=""
        content={content}
        isHeaderLayout={false}
      />

      <Notification
        open={state.statusDisplay}
        message={state.statusDisplayText}
        closeStatusDisplay={handleCloseNotification}
      />
    </div>
  );
};

CrewGroupDefault.propTypes = {
  user: PropTypes.object,
  bidSession: PropTypes.object
};

CrewGroupDefault.defaultProps = {
  user: {},
  bidSession: {}
};

const mapStateToProps = state => ({
  user: state.bidSession.user,
  bidSession: state.bidSession
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchBidPreferences: (staffId, bidSessionId) =>
    fetchBidPreferencesAction(sessionStorage.getItem('staffId') || staffId || ownProps.user?.staffId, bidSessionId)(),
  // Override bidSessionId with CREW_GROUP_DEFAULT_BIDSESSION_ID from sessionStorage for Crew Group Default context
  fetchClassOptionAttributes: (optionClassId, bidSessionId, openCombinedPreferences) => {
    const crewGroupDefaultBidSessionId = sessionStorage.getItem(CREW_GROUP_DEFAULT_BIDSESSION_ID);
    const useBidSessionId = crewGroupDefaultBidSessionId || bidSessionId;
    return dispatch(bidClassesProductionActions.fetchClassOptionAttributes(optionClassId, useBidSessionId, openCombinedPreferences));
  },
  fetchCrewGroups: () =>
    dispatch(fetchCrewGroupsAction()),
  savePreference: (bidPreference) => {
    // For Crew Group Default, use staffId from sessionStorage if available
    if (sessionStorage.getItem(CREW_GROUP_DEFAULT_BIDSESSION_ID)) {
      bidPreference.staffId = getStaffId() || ownProps.user?.staffId;
    }
    return dispatch(savePreferenceAction(bidPreference));
  },
  deletePreference: (bidId, employeeBidId, adminComments, adminName) => {
    // For Crew Group Default, use staffId from sessionStorage if available
    let staffId = ownProps.user?.staffId;
    if (sessionStorage.getItem(CREW_GROUP_DEFAULT_BIDSESSION_ID)) {
      staffId = getStaffId();
    }
    // If deletePreference needs staffId, add it to the API call or payload as required
    return dispatch(bidClassesProductionActions.deletePreference(bidId, employeeBidId, adminComments, adminName, staffId));
  },
  switchPriority: (bidAggregateIds, bidAggregateIdsToSwitchWith) =>
    dispatch(switchPriorityAction(bidAggregateIds, bidAggregateIdsToSwitchWith)),
  updateAllScores: (preferences) =>
    dispatch(updateAllScoresAction(preferences)),
  updateWeight: (preference, newScore, bidMode, prefIds) =>
    dispatch(updateWeightAction(preference, newScore, bidMode, prefIds)),
  updateWeightAll: (preference, newScore, bidMode, prefIds) =>
    dispatch(updateWeightForAllAction(preference, newScore, bidMode, prefIds)),
  updateBidSession: bidSession => dispatch(updateBidSessionSuccess(bidSession))
});

export default connect(mapStateToProps, mapDispatchToProps)(CrewGroupDefault);
